import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import type { Area } from "react-easy-crop";

interface ImageCropperProps {
  imageSrc: string;
  onCropComplete: (croppedBlob: Blob) => void;
  onCancel: () => void;
}

export default function ImageCropper({ imageSrc, onCropComplete, onCancel }: ImageCropperProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [processing, setProcessing] = useState(false);

  const onCropChange = useCallback((location: { x: number; y: number }) => {
    setCrop(location);
  }, []);

  const onZoomChange = useCallback((zoom: number) => {
    setZoom(zoom);
  }, []);

  const onCropAreaComplete = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  async function handleCrop() {
    if (!croppedAreaPixels) return;
    setProcessing(true);

    try {
      const blob = await getCroppedImg(imageSrc, croppedAreaPixels);
      onCropComplete(blob);
    } catch (e) {
      console.error("Kırpma hatası:", e);
      alert("Resim kırpılamadı. Lütfen tekrar dene.");
    } finally {
      setProcessing(false);
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.9)",
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Crop alanı */}
      <div style={{ position: "relative", flex: 1, minHeight: 0 }}>
        <Cropper
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          aspect={1}
          cropShape="round"
          showGrid={false}
          onCropChange={onCropChange}
          onZoomChange={onZoomChange}
          onCropComplete={onCropAreaComplete}
        />
      </div>

      {/* Kontroller */}
      <div
        style={{
          padding: "20px 24px",
          backgroundColor: "#1a1a1a",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        {/* Zoom slider */}
        <div>
          <label
            htmlFor="zoom-slider"
            style={{ display: "block", marginBottom: 8, fontSize: 14, color: "#fff" }}
          >
            Yakınlaştır
          </label>
          <input
            id="zoom-slider"
            type="range"
            min={1}
            max={3}
            step={0.1}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            style={{ width: "100%" }}
          />
        </div>

        {/* Butonlar */}
        <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
          <button
            onClick={onCancel}
            disabled={processing}
            style={{
              padding: "10px 20px",
              background: "#333",
              color: "#fff",
              border: "1px solid #555",
              borderRadius: 6,
              cursor: processing ? "not-allowed" : "pointer",
            }}
          >
            İptal
          </button>
          <button
            onClick={handleCrop}
            disabled={processing}
            style={{
              padding: "10px 20px",
              background: "#0070f3",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              cursor: processing ? "not-allowed" : "pointer",
              fontWeight: 600,
            }}
          >
            {processing ? "İşleniyor..." : "Kırp ve Kaydet"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Helper: Canvas üzerinde kırpma işlemi
// ────────────────────────────────────────────────────────────
async function getCroppedImg(imageSrc: string, pixelCrop: Area): Promise<Blob> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    throw new Error("Canvas context alınamadı");
  }

  // Canvas boyutunu kırpılan alana eşitle
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  // Resmi kırpılmış alanda çiz
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Canvas'tan blob oluşturulamadı"));
        return;
      }
      resolve(blob);
    }, "image/webp");
  });
}

function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.src = url;
  });
}
